import gql from 'graphql-tag';
import { useQuery, useResult, useMutation } from '@vue/apollo-composable';
import { cloneDeep } from 'lodash-es';
import { ref, computed } from 'vue';
import { useRoute } from 'vue-router';
import { useNPPackageInfo } from '@models/package/package';
import { usePackageInfo } from '@models/package/usePackageInfo';
import { formatExperienceInfo, judgeHasSync } from '@experience/hooks/setting.hooks';

export type ExperienceStatusType = 'PAUSE' | 'SCHEDULED' | 'DRAFT' | 'RUNNING' | 'UNSCHEDULED';
export type TrafficModeType = 'AVERAGE' | 'CUSTOM' | 'INTELLIGENT';
export enum DeviceType {
    MOBILE = 1,
    PC = 2
}

export enum TerminalStatus {
    OFF = 0,
    ON = 1
}

export type ExperienceType =
    | 'POPUP'
    | 'STICKY_BAR'
    | 'INLINE'
    | 'ADVANCED'
    | 'REDIRECT'
    | 'DEFAULT';

export interface Usergroup {
    type: 'USER_GROUP' | 'CONDITION';
    value: any;
}

export interface Redirect {
    condition: string;
    value: string;
    toValue: string;
    previewUrl: string;
    advanceEnable?: boolean;
}

export interface ExperienceVersion {
    id: string;
    name: string;
    rate: number;
    controlGroup: boolean;
    screenshot: string;
    engageID: string;
    terminals: ExperienceCreativeTerminal[];
}

export interface ExperienceCreativeTerminal {
    id: string;
    type: DeviceType;
    status: TerminalStatus;
    popup: any;
    stickybar: any;
    code: any;
    inline: any;
    engageVersionID: string;
    redirect: Redirect;
}

export interface Goal {
    id: string;
    sid: string;
    name: string;
    content: any;
    description: string;
    createTime: number;
    updateTime: number;
    createBy: string;
    modifierBy: string;
    status: number;
    count: number;
}
export interface Experience {
    id: string;
    sid: string;
    name: string;
    status: ExperienceStatusType;
    createTime: number;
    updateTime: number;
    lastPauseTime: number;
    firstRunTime: number;
    hasSync: boolean;
    hasForm: boolean;
    isVersionEvent: boolean;
    trafficMode: TrafficModeType;
    createBy: string;
    modifierBy: string;
    enableABTest: boolean;
    hasVariant: boolean;
    calculateConfidence?: number;
    calculateDays?: number;
    engageVersion: string;
    engageType: ExperienceType;
    baseURL: string;
    screenshot: string;
    schedule: any;
    conditions: any;
    usergroups: Usergroup[];
    versions: ExperienceVersion[];
    goals: Goal[];
    count: number;
    trigger: any;
    templateSource?: {
        id?: string;
        name?: string;
        type?: string;
        purpos?: string[];
        industry?: string[];
    };
}

export type CreateExperienceV4Input = {
    id: string;
    name: string;
    engageType?: ExperienceType;
    isABTest: boolean;
    baseUrl?: string;
    schedule: any;
    conditions: any;
    baseURL: String;
    usergroups?: Usergroup[];
    trigger: any;
    versions?: ExperienceVersion[];
    templateSource?: {
        id?: string;
        name?: string;
        type?: string;
        purpos?: string[];
        industry?: string[];
    };
};

export type UpdateEngageV4Input = {
    name?: string;
    status?: ExperienceStatusType;
    trafficMode?: TrafficModeType;
    hasSync?: boolean;
    hasVariant?: boolean;
    engageType?: ExperienceType;
    hasForm?: boolean;
    isVersionEvent?: boolean;
    baseURL?: string;
    schedule?: any;
    conditions?: any;
    usergroups?: Usergroup[];
    versions?: ExperienceVersion[];
    goals?: Goal[];
    enableABTest?: boolean;
    calculateConfidence?: number;
    calculateDays?: number;
};

export const EngageV4Type = `{
    id
    sid
    name
    status
    createTime
    updateTime
    lastPauseTime
    firstRunTime
    hasSync
    trafficMode
    hasForm
    isVersionEvent
    createBy
    modifierBy
    hasVariant
    calculateConfidence
    calculateDays
    engageVersion
    engageType
    enableABTest
    baseURL
    screenshot
    schedule
    conditions
    trigger
    goals {
        id
        name
    }
    usergroups {
        type
        value
    }
    versions {
        id
        name
        rate
        controlGroup
        screenshot
        terminals {
            id
            type
            popup
            stickybar
            inline
            code
            status
            redirect
        }
    }
    count
    templateSource
}
`;

const EngageV4TypePublish = `{
    engageType
    enableABTest
    templateSource
}`;

export const EXPERIENCE_QUERY = gql`
    query getEngageV4($id: ID!) {
        getEngageV4(id: $id) ${EngageV4Type}
    }
`;

const CREATE_EXPERIENCE_MUTATION = gql`
    mutation createEngageV4($input: CreateEngageV4Input!) {
        createEngageV4(input: $input) ${EngageV4Type}
    }
`;

const UPDATE_EXPERIENCE_MUTATION = gql`
    mutation updateEngageV4($input: UpdateEngageV4Input!) {
        updateEngageV4(input: $input)
    }
`;
// graphql - experience暂停
const PAUSE_EXPERIENCE_MUTATION = gql`
    mutation pauseEngageV4($id: ID!) {
        pauseEngageV4(id: $id)
    }
`;
// graphql - experience发布
const PUBLISH_EXPERIENCE_MUTATION = gql`
    mutation publishEngageV4($id: ID!) {
        publishEngageV4(id: $id) ${EngageV4TypePublish}
    }
`;
// graphql - experience更新
const SCHEDULE_EXPERIENCE_MUTATION = gql`
    mutation saveAndScheduleEngageV4($id: ID!) {
        saveAndScheduleEngageV4(id: $id)
    }
`;
// graphql - experience删除
const DELETE_EXPERIENCE_MUTATION = gql`
    mutation deleteEngageV4($id: ID!) {
        deleteEngageV4(id: $id) ${EngageV4Type}
    }
`;

// graphql - experience复制
const COPY_EXPERIENCE_MUTATION = gql`
    mutation copyEngageV4($id: ID!) {
        copyEngageV4(id: $id) ${EngageV4Type}
    }
`;

const { getProfilePackageInfo } = usePackageInfo();
const { getProfileNpPackageInfo } = useNPPackageInfo();

let experienceInfo = ref<Experience | null>();
let experienceLoading = ref(false);
let experienceInfoResult = ref<Experience | null>(); // format前的值

export function useExperience() {
    const route = useRoute();
    const experienceId = computed(() => {
        return route.params?.experienceId;
    });

    // 创建 Experience
    const {
        mutate: createExperienceMutate,
        loading: createExperienceLoading,
        error: createExperienceError,
        onError: onCreateExperienceError
    } = useMutation(CREATE_EXPERIENCE_MUTATION, {
        errorPolicy: 'all'
    });

    function createExperience(
        param: CreateExperienceV4Input,
        onSuccess?: (res?: any) => void,
        onFailed?: (err?: any) => void
    ) {
        createExperienceMutate({ input: param }).then(
            result => {
                getProfilePackageInfo();
                getProfileNpPackageInfo();
                onSuccess &&
                    typeof onSuccess === 'function' &&
                    onSuccess(result?.data?.createEngageV4);
            },
            err => {
                onFailed && typeof onFailed === 'function' && onFailed(err);
            }
        );
    }

    function getExperience(
        experienceIdparam?: string,
        resultCallback?: (T: Experience | null) => void,
        rejectCallback?: (err: any) => void
    ) {
        const id = experienceId.value || experienceIdparam;
        if (id) {
            experienceLoading.value = true;

            const {
                result,
                // loading: experienceLoading,
                onError,
                onResult
            } = useQuery<{ getEngageV4: Experience }>(
                EXPERIENCE_QUERY,
                () => {
                    return {
                        id
                    };
                },
                {
                    fetchPolicy: 'no-cache' //cache-and-network cache-first
                }
            );
            onResult(data => {
                if (data?.data?.getEngageV4) {
                    experienceInfoResult.value = data.data.getEngageV4;
                    experienceInfo.value = formatExperienceInfo(data.data.getEngageV4);
                    // 执行回调
                    resultCallback &&
                        typeof resultCallback === 'function' &&
                        resultCallback(experienceInfo.value);
                    experienceLoading.value = false;
                }
            });
            onError(err => {
                rejectCallback && typeof rejectCallback === 'function' && rejectCallback(err);
                experienceLoading.value = false;
            });
        }
    }

    const experienceOtherInfo = computed(() => {
        if (!experienceInfo.value) return null;
        const { engageType, hasForm, enableABTest, status, trafficMode } = experienceInfo.value;
        const { usergroups } = experienceInfoResult.value || {};
        const hasPopup = getPopup(experienceInfo);
        return {
            engageType,
            hasPopup,
            hasForm: hasForm,
            hasAbtest: enableABTest,
            isRedirectTest: engageType?.toLocaleUpperCase() === 'REDIRECT',
            visibleTrigger: ['POPUP', 'STICKY_BAR'].includes(
                engageType?.toLocaleUpperCase() as string
            ),
            visiblePageCondition: (engageType?.toLocaleUpperCase() as string) !== 'REDIRECT',
            isRunning: status === 'RUNNING',
            isDraft: status === 'DRAFT',
            isRunningOrScheduled: ['RUNNING', 'SCHEDULED'].includes(status),
            isCustomTrafficMode: trafficMode === 'CUSTOM',
            isInterlligentTrafficMode: trafficMode === 'INTELLIGENT',
            isInlineOrAdvance: ['INLINE', 'ADVANCED'].includes(
                engageType?.toLocaleUpperCase() as string
            ),
            isUnknowType: engageType?.toLocaleUpperCase() === 'DEFAULT',
            isAllPeople:
                !usergroups ||
                usergroups.length <= 0 ||
                usergroups.some(
                    item =>
                        item.type === 'USER_GROUP' && item.value.some(i => i.source === 'default')
                )
        };
    });

    function getPopup(data: any) {
        if (['POPUP', 'STICKY_BAR'].includes(data.value?.engageType?.toLocaleUpperCase())) {
            return true;
        } else if (data.value?.engageType === 'INLINE') {
            return false;
        } else if (data.value?.engageType === 'ADVANCED') {
            return data.value.versions?.some((version: any) => {
                return version.terminals?.some((terminal: any) => {
                    return (
                        (terminal.popup && terminal.popup.length > 0) ||
                        (terminal.stickybar && terminal.stickybar.length > 0)
                    );
                });
            });
        } else {
            return null;
        }
    }

    // 更新且更新Experience
    const {
        mutate: updateExperienceMutate,
        loading: updateExperienceMutateLoading,
        error: updateExperienceError
    } = useMutation(UPDATE_EXPERIENCE_MUTATION);
    const updateExperience = (param: UpdateEngageV4Input, onSuccess?: (res?: any) => void) => {
        // running或schedule状态下，判断内容有没有更新，如果有更新hasSync设置为false
        if (experienceOtherInfo.value?.isRunningOrScheduled) {
            const hasUpdate = judgeHasSync(param, experienceInfoResult.value);
            hasUpdate && (param['hasSync'] = false);
        }
        if (param['hasForm'] !== undefined && !param['hasForm']) {
            delete param['hasForm'];
        }
        updateExperienceMutate(
            { input: { id: route.params?.experienceId || experienceId.value, ...param } },
            {
                update: (cache, { data: { updateEngageV4 } }) => {
                    if (experienceInfo.value) {
                        for (const key in param) {
                            type KeyType = keyof UpdateEngageV4Input;
                            const initialValue = param[key as KeyType];
                            (experienceInfo.value[key as KeyType] as any) = formatExperienceInfo(
                                cloneDeep(initialValue)
                            );
                        }
                    }
                    onSuccess && typeof onSuccess === 'function' && onSuccess(experienceInfo.value);
                }
            }
        );
    };

    // 删除 Experience
    const {
        mutate: deleteExperienceMutate,
        loading: deleteExperienceLoading,
        onError: deleteExperienceError
    } = useMutation(DELETE_EXPERIENCE_MUTATION);
    const deleteExperience = (id: string, onSuccess?: (res?: any) => void) => {
        return deleteExperienceMutate({ id }).then((result: any) => {
            onSuccess && typeof onSuccess === 'function' && onSuccess(result?.data?.deleteEngageV4);
            getProfilePackageInfo();
            getProfileNpPackageInfo();
            return result.data.deleteEngageV4;
        });
    };

    // 暂停experience
    const {
        mutate: pauseExperienceMutate,
        loading: pauseExperienceLoading,
        onError: pauseExperienceError
    } = useMutation(PAUSE_EXPERIENCE_MUTATION);

    const pauseExperience = (id: string, onSuccess?: (res?: any) => void) => {
        return pauseExperienceMutate({ id }).then((result: any) => {
            onSuccess && typeof onSuccess === 'function' && onSuccess(result?.data?.pauseEngageV4);
            return result.data.pauseEngageV4;
        });
    };

    // schedule experience
    const {
        mutate: scheduleExperienceMutate,
        loading: scheduleExperienceLoading,
        onError: onScheduleExperienceError,
        onDone: onScheduleExperienceDone
    } = useMutation(SCHEDULE_EXPERIENCE_MUTATION);

    const scheduleExperience = (id: string, onSuccess?: (res?: any) => void) => {
        scheduleExperienceMutate({ id }).then(result => {
            onSuccess &&
                typeof onSuccess === 'function' &&
                onSuccess(result?.data?.saveAndScheduleEngageV4);
        });
    };

    // 发布experience
    const {
        mutate: publishExperienceMutate,
        loading: publishExperienceLoading,
        onError: publishExperienceError
    } = useMutation(PUBLISH_EXPERIENCE_MUTATION);

    const publishExperience = (id: string, onSuccess?: (res?: any) => void) => {
        return publishExperienceMutate({ id }).then((result: any) => {
            onSuccess &&
                typeof onSuccess === 'function' &&
                onSuccess(result?.data?.publishEngageV4);
            return result.data.publishEngageV4;
        });
    };

    // 复制
    const {
        mutate: copyExperienceMutate,
        loading: copyExperienceLoading,
        onError: copyExperienceError
    } = useMutation(COPY_EXPERIENCE_MUTATION);
    const copyExperience = (id: string, onSuccess?: (res?: any) => void) => {
        return copyExperienceMutate({ id }).then((result: any) => {
            onSuccess && typeof onSuccess === 'function' && onSuccess(result?.data?.copyEngageV4);
            getProfilePackageInfo();
            getProfileNpPackageInfo();
            return result.data.copyEngageV4;
        });
    };

    return {
        experienceInfoResult,
        experienceInfo,
        experienceOtherInfo,
        experienceLoading,
        createExperienceLoading,
        deleteExperienceLoading,
        pauseExperienceLoading,
        updateExperienceMutateLoading,
        scheduleExperienceLoading,
        publishExperienceLoading,
        copyExperienceLoading,
        updateExperienceError,

        getExperience,
        createExperience,
        updateExperience,
        pauseExperience,
        publishExperience,
        scheduleExperience,
        onScheduleExperienceDone,
        onScheduleExperienceError,
        deleteExperience,
        copyExperience
    };
}
